import { useState, useEffect, useCallback, useMemo } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { BookOpen, ChevronLeft, ChevronRight, LogOut, Search, FileText, ScrollText, Shield, GitBranch, Users, Key } from 'lucide-react'
import WikiTree from './WikiTree'
import {
  search as searchApi,
  fetchConfig,
  fetchDocMarkdown,
  fetchWikiTree,
  fetchWikiShelves,
  adminListPending,
} from '../api/client'
import type { WikiTree as WikiTreeData, WikiShelf } from '../types/wiki'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'

const SIDEBAR_KEY = 'fda_sidebar_expanded'

export default function Shell() {
  const { logout, username, role: userRole } = useAuth()
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const s = localStorage.getItem(SIDEBAR_KEY)
      return s !== null ? s === 'true' : true
    } catch {
      return true
    }
  })
  const [wikiTree, setWikiTree] = useState<WikiTreeData | null>(null)
  const [wikiAvailable, setWikiAvailable] = useState(false)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<{ path: string; title: string; slug?: string }[]>([])
  const [shelves, setShelves] = useState<WikiShelf[]>([])
  const [shelfSlug, setShelfSlug] = useState('')
  const [bookSlug, setBookSlug] = useState('')
  const [convOpen, setConvOpen] = useState(false)
  const [convMd, setConvMd] = useState<string | null>(null)
  const [convErr, setConvErr] = useState<string | null>(null)
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(expanded))
    } catch {
      /* ignore */
    }
  }, [expanded])

  useEffect(() => {
    void fetchWikiTree()
      .then((tree) => {
        setWikiTree(tree)
        setWikiAvailable(tree.shelves.length > 0)
        return fetchWikiShelves()
          .then(({ shelves: s }) => setShelves(s.length > 0 ? s : tree.shelves))
          .catch(() => setShelves(tree.shelves))
      })
      .catch(() => {
        setWikiTree(null)
        setWikiAvailable(false)
        setLoadErr('Wiki tree unavailable')
      })
  }, [])

  useEffect(() => {
    if (!convOpen) {
      setConvMd(null)
      setConvErr(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { conventionsPath } = await fetchConfig()
        const body = await fetchDocMarkdown(conventionsPath)
        if (!cancelled) setConvMd(body)
      } catch {
        if (!cancelled) setConvErr('Could not load conventions document.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [convOpen])

  useEffect(() => {
    if (userRole === 'admin') {
      adminListPending()
        .then((changes: any[]) => setPendingCount(changes.length))
        .catch(() => setPendingCount(0))
    }
  }, [userRole])

  const runSearch = useCallback(
    async (query: string) => {
      const t = query.trim()
      if (t.length < 2) {
        setHits([])
        return
      }
      try {
        const filters =
          wikiAvailable && (shelfSlug || bookSlug)
            ? { shelfSlug: shelfSlug || undefined, bookSlug: bookSlug || undefined }
            : undefined
        const { hits: h } = await searchApi(t, 80, filters)
        setHits(h.map((x: { path: string; title: string; slug?: string }) => ({ path: x.path, title: x.title, slug: x.slug })))
      } catch {
        setHits([])
      }
    },
    [wikiAvailable, shelfSlug, bookSlug]
  )

  useEffect(() => {
    const id = window.setTimeout(() => {
      void runSearch(q)
    }, 320)
    return () => window.clearTimeout(id)
  }, [q, runSearch])

  const booksForShelf = useMemo(() => {
    if (!shelfSlug || !wikiTree) return []
    const shelf = wikiTree.shelves.find((s) => s.slug === shelfSlug)
    return shelf?.books ?? []
  }, [shelfSlug, wikiTree])

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 print:h-auto print:min-h-0 print:block print:bg-white">
      <aside
        className={`no-print flex flex-col shrink-0 bg-gray-950 border-r border-gray-800/60 overflow-hidden transition-[width] duration-200 ease-in-out ${
          expanded ? 'w-[280px]' : 'w-14'
        }`}
      >
        <div className="flex items-center justify-between gap-1 px-2 py-3 border-b border-gray-800/60 min-h-[52px]">
          {expanded ? (
            <div className="flex items-center gap-2 min-w-0 pl-1">
              <BookOpen className="w-6 h-6 text-primary-400 shrink-0" />
              <span className="font-semibold text-gray-100 truncate text-sm">Doc Archive</span>
            </div>
          ) : (
            <BookOpen className="w-6 h-6 text-primary-400 mx-auto shrink-0" />
          )}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100 shrink-0"
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {expanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {expanded && wikiAvailable && (
          <div className="px-2 py-2 border-b border-gray-800/60 space-y-2">
            {wikiAvailable && shelves.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <select
                  value={shelfSlug}
                  onChange={(e) => {
                    setShelfSlug(e.target.value)
                    setBookSlug('')
                  }}
                  aria-label="Filter by shelf"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2 text-xs text-gray-100 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All shelves</option>
                  {shelves.map((s) => (
                    <option key={s.id} value={s.slug}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {shelfSlug && booksForShelf.length > 0 && (
                  <select
                    value={bookSlug}
                    onChange={(e) => setBookSlug(e.target.value)}
                    aria-label="Filter by book"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2 text-xs text-gray-100 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All books</option>
                    {booksForShelf.map((b) => (
                      <option key={b.id} value={b.slug}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search wiki…"
                aria-label="Search wiki"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
              />
            </div>
            {q.trim().length >= 2 && hits.length > 0 && (
              <div className="rounded-lg border border-gray-700/60 bg-gray-900/60 overflow-hidden">
                <ul className="divide-y divide-gray-800/60 max-h-72 overflow-y-auto">
                  {hits.slice(0, 8).map((h) => (
                    <li key={h.path}>
                      {h.slug ? (
                        <NavLink
                          to={`/p/${h.slug}`}
                          className="block px-3 py-2 text-sm text-gray-300 hover:bg-gray-800/60 hover:text-gray-100 min-h-[44px]"
                          title={h.path}
                        >
                          <span className="truncate block">{h.title}</span>
                          <span className="text-xs text-gray-500 font-mono truncate block">{h.path}</span>
                        </NavLink>
                      ) : (
                        <div className="block px-3 py-2 text-sm text-gray-300 min-h-[44px]" title={h.path}>
                          <span className="truncate block">{h.title}</span>
                          <span className="text-xs text-gray-500 font-mono truncate block">{h.path}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {hits.length > 8 && (
                  <p className="text-xs text-gray-500 px-3 py-1.5 border-t border-gray-800/60">
                    Showing 8 of {hits.length} matches
                  </p>
                )}
              </div>
            )}
            {q.trim().length >= 2 && hits.length === 0 && (
              <p className="text-xs text-gray-500 px-0.5">No matches for “{q.trim()}”.</p>
            )}
          </div>
        )}

        {expanded && (
          <div className="flex-1 overflow-y-auto px-1 py-2 min-h-0">
            {wikiAvailable && wikiTree ? (
              <WikiTree tree={wikiTree} />
            ) : loadErr ? (
              <p className="text-error-400 text-sm px-2">{loadErr}</p>
            ) : null}
          </div>
        )}

        {expanded && (
          <div className="border-t border-gray-800/60 px-2 py-1 space-y-0.5">
            <button
              type="button"
              onClick={() => setConvOpen(true)}
              className="flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-800/60 hover:text-gray-200"
            >
              <ScrollText className="w-4 h-4 shrink-0" />
              <span>Doc conventions</span>
            </button>
            <NavLink
              to="/audit"
              className={({ isActive }) =>
                `flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm ${
                  isActive
                    ? 'bg-primary-900/40 text-primary-200 border border-primary-700/40'
                    : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-200 border border-transparent'
                }`
              }
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span>Audit log</span>
            </NavLink>
          </div>
        )}

        {userRole === 'admin' && expanded && (
          <div className="border-t border-gray-800/60 px-2 py-1 space-y-0.5">
            <div className="flex items-center gap-2 px-2 py-1.5">
              <Shield className="w-4 h-4 text-amber-500/80" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Admin</span>
            </div>
            <NavLink
              to="/admin/users"
              className={({ isActive }) =>
                `flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm ${
                  isActive
                    ? 'bg-primary-900/40 text-primary-200 border border-primary-700/40'
                    : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-200 border border-transparent'
                }`
              }
            >
              <Users className="w-4 h-4 shrink-0" />
              <span>Users</span>
            </NavLink>
            <NavLink
              to="/admin/permissions"
              className={({ isActive }) =>
                `flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm ${
                  isActive
                    ? 'bg-primary-900/40 text-primary-200 border border-primary-700/40'
                    : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-200 border border-transparent'
                }`
              }
            >
              <Key className="w-4 h-4 shrink-0" />
              <span>Permissions</span>
            </NavLink>
            <NavLink
              to="/admin/pending"
              className={({ isActive }) =>
                `flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm ${
                  isActive
                    ? 'bg-primary-900/40 text-primary-200 border border-primary-700/40'
                    : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-200 border border-transparent'
                }`
              }
            >
              <GitBranch className="w-4 h-4 shrink-0" />
              <span>Pending</span>
              {pendingCount > 0 && (
                <span className="ml-auto bg-amber-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          </div>
        )}

        {expanded && (
          <div className="border-t border-gray-800/60 p-2 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500 truncate" title={username ?? ''}>
              {username}
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </aside>

      <main className="flex-1 overflow-y-auto min-w-0 print:overflow-visible">
        <Outlet />
      </main>

      {convOpen && (
        <div
          className="no-print fixed inset-0 z-50 flex justify-end bg-black/50"
          role="presentation"
          onClick={() => setConvOpen(false)}
        >
          <div
            className="w-full max-w-lg h-full bg-gray-950 border-l border-gray-800 shadow-xl overflow-y-auto p-4 text-gray-200"
            role="dialog"
            aria-label="Documentation conventions"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Doc conventions</h2>
              <button
                type="button"
                onClick={() => setConvOpen(false)}
                className="text-sm text-gray-400 hover:text-gray-100"
              >
                Close
              </button>
            </div>
            {convErr && <p className="text-error-400 text-sm">{convErr}</p>}
            {convMd && (
              <div className="prose-invert prose-sm max-w-none [&_a]:text-primary-400">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                  {convMd}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
